import { useState, useEffect, useContext, useMemo } from "react";
import PrivateLayout from "@/components/layout/PrivateLayout";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";
import { FilterConfig, FilterOption } from "@/components/ui/filter-dropdown";
import { RefreshCw, FileText, Calendar, Edit3, Map } from "lucide-react";

import {
  TravelRequestCard,
  TravelRequestsTable,
  SortTravelRequestButton,
  ViewTravelRequestButton,
  FilterTravelRequestButton,
  TravelRequestModal,
  type SortMode,
  type ViewMode
} from "@/lib/requests";
import { Modal } from "@/components/ui/modal";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { TabsHeader } from "@/components/ui/tabs-header";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { useSidebar } from "@/contexts/SidebarContext";
import { useItineraries } from "@/hooks/useItineraries";
import { ItineraryCard } from "@/lib/itineraries/ItineraryCard";
import { AuthContext } from "@/lib/auth/models/auth.model";
import { TravelDesignerFilter } from "@/lib/itineraries/TravelDesignerFilter";
import { useTravelRequestsInfo } from "@/hooks/useTravelRequestsInfo";

const Dashboard = () => {
  const { t } = useI18n();
  // Crea opzioni stabili per evitare loop infiniti
  const draftItinerariesOptions = useMemo(() => ({ excludeStatus: 'completed' }), []);
  const completedItinerariesOptions = useMemo(() => ({ statusFilter: 'completed' }), []);

  const {
    loading,
    error,
    acceptTravelRequest,
    declineTravelRequest,
    travelRequests,
    refreshData: refreshTravelRequestsInfo
  } = useTravelRequestsInfo();

  const {
    itineraries,
    loading: itinerariesLoading,
    error: itinerariesError,
    refreshData: refreshItineraries,
    deleteItinerary,
    duplicateItinerary,
    updateItineraryStatus
  } = useItineraries(draftItinerariesOptions);

  const {
    itineraries: completedItineraries,
    loading: completedItinerariesLoading,
    error: completedItinerariesError,
    refreshData: refreshCompletedItineraries,
    deleteItinerary: deleteCompletedItinerary,
    duplicateItinerary: duplicateCompletedItinerary,
    updateItineraryStatus: updateCompletedItineraryStatus
  } = useItineraries(completedItinerariesOptions);


  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useItineraries deve essere usato all\'interno di un AuthProvider');
  }
  const userRole = authContext.user.user_metadata.role;

  const [refreshingRequests, setRefreshingRequests] = useState(false);
  const [refreshingItineraries, setRefreshingItineraries] = useState(false);
  const [refreshingCompletedItineraries, setRefreshingCompletedItineraries] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = searchParams.get('tab') || 'requests';
  const { isSidebarCollapsed } = useSidebar();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [filters, setFilters] = useState<FilterConfig[]>([]);
  const [selectedTravelRequest, setSelectedTravelRequest] = useState<TravelRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateItineraryModalOpen, setIsCreateItineraryModalOpen] = useState(false);
  const [selectedRequestForItinerary, setSelectedRequestForItinerary] = useState<TravelRequest | null>(null);
  const [selectedDesigner, setSelectedDesigner] = useState<string | null>(null);
  const itemsPerPage = 5;

  // Configurazione opzioni filtro
  const filterOptions: FilterOption[] = [
    {
      value: 'destinations',
      label: 'Destinations',
      operations: [{ value: 'contains', label: 'Contains' }]
    },
    {
      value: 'customer_name',
      label: 'Customer Name',
      operations: [{ value: 'contains', label: 'Contains' }]
    },
    {
      value: 'budget',
      label: 'Budget',
      operations: [
        { value: 'greater_than', label: '>' },
        { value: 'less_than', label: '<' },
        { value: 'greater_equal', label: '>=' },
        { value: 'less_equal', label: '<=' }
      ]
    },
    {
      value: 'guests',
      label: 'Guests',
      operations: [
        { value: 'equals', label: '=' },
        { value: 'greater_than', label: '>' },
        { value: 'less_than', label: '<' },
        { value: 'greater_equal', label: '>=' },
        { value: 'less_equal', label: '<=' }
      ]
    },
    {
      value: 'departure',
      label: 'Departure',
      operations: [{ value: 'contains', label: 'Contains' }]
    },
    {
      value: 'travel_start_date',
      label: 'Travel Start Date',
      is_date: true,
      operations: []
    },
    {
      value: 'travel_end_date',
      label: 'Travel End Date',
      is_date: true,
      operations: []
    },
    {
      value: 'created_at',
      label: 'Created Date',
      is_date: true,
      operations: []
    }
  ];

  // Refresh specifico per le travel requests
  const handleRefreshRequests = async () => {
    setRefreshingRequests(true);
    await refreshTravelRequestsInfo();
    setRefreshingRequests(false);
  };

  // Refresh specifico per gli itinerari
  const handleRefreshItineraries = async () => {
    setRefreshingItineraries(true);
    await refreshItineraries();
    setRefreshingItineraries(false);
  };

  // Refresh specifico per gli itinerari completati
  const handleRefreshCompletedItineraries = async () => {
    setRefreshingCompletedItineraries(true);
    await refreshCompletedItineraries();
    setRefreshingCompletedItineraries(false);
  };

  const handleViewRequest = (requestId: string) => {
    const travelRequest = displayedTravelRequests.find(req => req.id === requestId);
    if (travelRequest) {
      setSelectedTravelRequest(travelRequest);
      setIsModalOpen(true);
    }
  };

  const handleCreateItinerary = (requestId: string) => {
    const travelRequest = displayedTravelRequests.find(req => req.id === requestId);
    if (travelRequest) {
      setSelectedRequestForItinerary(travelRequest);
      setIsCreateItineraryModalOpen(true);
    }
  };

  const handleStartBuilding = () => {
    // Navigate to itinerary builder page with request ID as query param
    if (selectedRequestForItinerary) {
      navigate(`/itinerary-builder?requestId=${selectedRequestForItinerary.id}`);
    } else {
      navigate('/itinerary-builder');
    }
  };

  const handleCancelItineraryCreation = () => {
    setIsCreateItineraryModalOpen(false);
    setSelectedRequestForItinerary(null);
  };

  const handleEditItinerary = (itineraryId: string) => {
    // Navigate to itinerary builder with the itinerary ID
    navigate(`/itinerary-builder?itineraryId=${itineraryId}`);
  };

  const handleDeleteItinerary = async (itineraryId: string) => {
    await deleteItinerary(itineraryId);
  };

  const handleDuplicateItinerary = async (itineraryId: string) => {
    await duplicateItinerary(itineraryId);
  };

  const handleViewTravelRequest = (travelRequestId: string) => {
    const travelRequest = travelRequests.find(req => req.id === travelRequestId);
    if (travelRequest) {
      setSelectedTravelRequest(travelRequest);
      setIsModalOpen(true);
    }
  };

  const [displayedTravelRequests, setDisplayedTravelRequests] = useState<TravelRequest[]>(travelRequests);
  const [sortedTravelRequests, setSortedTravelRequests] = useState<TravelRequest[]>(travelRequests);

  // Filtered itineraries state - managed by TravelDesignerFilter component
  const [filteredDraftItineraries, setFilteredDraftItineraries] = useState<any[]>([]);
  const [filteredCompletedItineraries, setFilteredCompletedItineraries] = useState<any[]>([]);

  // Initialize filtered itineraries when data changes
  useEffect(() => {
    setFilteredDraftItineraries(itineraries);
  }, [itineraries]);

  useEffect(() => {
    setFilteredCompletedItineraries(completedItineraries);
  }, [completedItineraries]);

  // Inizializza i dati con l'ordinamento di default quando travelRequests cambia
  useEffect(() => {
    if (travelRequests.length > 0) {
      // Applica l'ordinamento di default (newest) ai dati iniziali
      const initialSortedTravelRequests = [...travelRequests].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setSortedTravelRequests(initialSortedTravelRequests);
      setDisplayedTravelRequests(initialSortedTravelRequests);
    } else {
      setSortedTravelRequests([]);
      setDisplayedTravelRequests([]);
    }
  }, [travelRequests]);


  const newRequests = displayedTravelRequests.filter(travelRequest => {
    const createdAt = new Date(travelRequest.created_at);
    const now = new Date();
    const diffInHours = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
    return diffInHours < 24; // Meno di 24 ore
  }).length;

  // Configurazione dei tab
  const tabs = [
    {
      value: "requests",
      label: t('dashboard.tabRequests'),
      icon: FileText,
      badge: travelRequests.length.toString()
    },
    {
      value: "drafts",
      label: t('dashboard.tabDrafts'),
      icon: Edit3,
      badge: itineraries.length.toString()
    },
    {
      value: "planned",
      label: t('dashboard.tabPlanned'),
      icon: Calendar,
      badge: completedItineraries.length.toString()
    }
  ];

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset paginazione quando cambia vista
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === 'grid') {
      setCurrentPage(1); // Reset per infinite scroll
    }
  };

  return (
    <PrivateLayout
      pageTitle={t('dashboard.pageTitle')}
      pageDescription={t('dashboard.pageDescription')}
      showBackButton={false}
    >
      <div className="space-y-6">
        {/* Reusable Tabs Header */}
        <TabsHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabs}
          sidebarCollapsed={isSidebarCollapsed}
        />

        {/* Tab Content */}
        <div className="max-w-7xl mx-auto">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <ProfileTabsContent value="requests">
              <div className="space-y-6">
                {/* Header con titolo e controlli */}
                <div className="space-y-4 sm:space-y-0 sm:flex sm:justify-between sm:items-center">
                  <div className="flex items-center gap-2">
                    <h1 className="text-sm text-gray-500">
                      {travelRequests.length ? t('dashboard.requestsCount', { new: newRequests, total: travelRequests.length }) : t('dashboard.noRequests')}
                    </h1>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <FilterTravelRequestButton
                      filters={filters}
                      onFiltersChange={setFilters}
                      options={filterOptions}
                      travelRequests={sortedTravelRequests}
                      onFilteredTravelRequestsChange={setDisplayedTravelRequests}
                      disabled={travelRequests.length === 0}
                    />
                    <SortTravelRequestButton
                      sortMode={sortMode}
                      onSortModeChange={setSortMode}
                      travelRequests={travelRequests}
                      onSortedTravelRequestsChange={setSortedTravelRequests}
                      filters={filters}
                      onFilteredTravelRequestsChange={setDisplayedTravelRequests}
                      disabled={travelRequests.length === 0}
                    />
                    <ViewTravelRequestButton
                      viewMode={viewMode}
                      onViewModeChange={handleViewModeChange}
                      disabled={travelRequests.length === 0}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefreshRequests}
                      disabled={refreshingRequests}
                      className="flex items-center gap-2"
                    >
                      <RefreshCw className={`h-4 w-4 ${refreshingRequests ? "animate-spin" : ""}`} />
                      <span className="hidden sm:inline">{t('dashboard.refresh')}</span>
                    </Button>
                  </div>
                </div>

                {/* Contenuto delle richieste di viaggio */}
                <div className="relative">
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {displayedTravelRequests.map((travelRequest) => (
                        <TravelRequestCard
                          key={travelRequest.id}
                          travelRequest={travelRequest}
                          userRole={userRole}
                          onViewRequest={handleViewRequest}
                          onCreateItinerary={handleCreateItinerary}
                        />
                      ))}
                    </div>
                  ) : (
                    <TravelRequestsTable
                      travelRequest={displayedTravelRequests}
                      currentPage={currentPage}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handlePageChange}
                      onViewRequest={handleViewRequest}
                      onCreateItinerary={handleCreateItinerary}
                    />
                  )}

                  {/* Overlay di caricamento */}
                  {loading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10 rounded-lg">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                  )}
                </div>

                {/* Errore */}
                {error && (
                  <div className="text-center py-12">
                    <p className="text-red-600">{t('dashboard.loadError')}</p>
                    <Button onClick={handleRefreshRequests} className="mt-4">
                      {t('dashboard.retry')}
                    </Button>
                  </div>
                )}

                {/* Stato vuoto - Nessun dato */}
                {!loading && !error && travelRequests.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-600 mb-4">{t('dashboard.noAssignedTrips')}</p>
                    <p className="text-sm text-gray-500 mb-4">{t('dashboard.noAssignedTripsDesc')}</p>
                  </div>
                )}

                {/* Stato vuoto - Nessun risultato dalla ricerca */}
                {!loading && !error && travelRequests.length > 0 && displayedTravelRequests.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-600 mb-4">{t('dashboard.noSearchResults')}</p>
                    <p className="text-sm text-gray-500 mb-4">{t('dashboard.noSearchResultsDesc')}</p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFilters([]);
                        setDisplayedTravelRequests(sortedTravelRequests);
                      }}
                    >
                      {t('dashboard.removeAllFilters')}
                    </Button>
                  </div>
                )}
              </div>
            </ProfileTabsContent>

            <ProfileTabsContent value="planned">
              <div className="space-y-6">
                {/* Header con titolo e controlli */}
                <div className="space-y-4 sm:space-y-0 sm:flex sm:justify-between sm:items-center">
                  <div className="flex items-center gap-2">
                    <h1 className="text-sm text-gray-500">
                      {completedItineraries.length ? t('dashboard.plannedCount', { count: completedItineraries.length }) : t('dashboard.noPlannedItineraries')}
                    </h1>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {userRole === "admin" && (
                      <TravelDesignerFilter
                        selectedDesigner={selectedDesigner}
                        onDesignerChange={setSelectedDesigner}
                        itineraries={completedItineraries}
                        onFilteredItinerariesChange={setFilteredCompletedItineraries}
                        disabled={completedItineraries.length === 0}
                      />
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefreshCompletedItineraries}
                      disabled={refreshingCompletedItineraries}
                      className="flex items-center gap-2"
                    >
                      <RefreshCw className={`h-4 w-4 ${refreshingCompletedItineraries ? "animate-spin" : ""}`} />
                      <span className="hidden sm:inline">{t('dashboard.refresh')}</span>
                    </Button>
                  </div>
                </div>

                {/* Contenuto degli itinerari completati */}
                <div className="relative">
                  {filteredCompletedItineraries.length > 0 ? (
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {filteredCompletedItineraries.map((itinerary) => (
                        <ItineraryCard
                          key={itinerary.id}
                          itinerary={itinerary}
                          onEdit={handleEditItinerary}
                          onDelete={deleteCompletedItinerary}
                          onDuplicate={duplicateCompletedItinerary}
                          onViewTravelRequest={handleViewTravelRequest}
                          onViewItinerary={handleEditItinerary}
                          onUpdateStatus={updateCompletedItineraryStatus}
                          travelRequests={travelRequests}
                          isAdmin={userRole === "admin"}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-600 mb-4">
                        {selectedDesigner
                          ? t('dashboard.noItinerariesForDesigner', { designer: selectedDesigner })
                          : t('dashboard.noCompletedItineraries')
                        }
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        {selectedDesigner
                          ? t('dashboard.tryDifferentDesigner')
                          : t('dashboard.completeFirstItinerary')
                        }
                      </p>
                      {selectedDesigner && (
                        <Button
                          variant="outline"
                          onClick={() => setSelectedDesigner(null)}
                          className="mt-2"
                        >
                          {t('dashboard.clearFilter')}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Overlay di caricamento */}
                  {completedItinerariesLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10 rounded-lg">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                  )}
                </div>

                {/* Errore */}
                {completedItinerariesError && (
                  <div className="text-center py-12">
                    <p className="text-red-600">{t('dashboard.loadCompletedError')}</p>
                    <Button onClick={handleRefreshCompletedItineraries} className="mt-4">
                      {t('dashboard.retry')}
                    </Button>
                  </div>
                )}
              </div>
            </ProfileTabsContent>

            <ProfileTabsContent value="drafts">
              <div className="space-y-6">
                {/* Header con titolo e controlli */}
                <div className="space-y-4 sm:space-y-0 sm:flex sm:justify-between sm:items-center">
                  <div className="flex items-center gap-2">
                    <h1 className="text-sm text-gray-500">
                      {itineraries.length ? t('dashboard.draftsCount', { count: itineraries.length }) : t('dashboard.noDraftItineraries')}
                    </h1>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {userRole === "admin" && (
                      <TravelDesignerFilter
                        selectedDesigner={selectedDesigner}
                        onDesignerChange={setSelectedDesigner}
                        itineraries={itineraries}
                        onFilteredItinerariesChange={setFilteredDraftItineraries}
                        disabled={itineraries.length === 0}
                      />
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefreshItineraries}
                      disabled={refreshingItineraries}
                      className="flex items-center gap-2"
                    >
                      <RefreshCw className={`h-4 w-4 ${refreshingItineraries ? "animate-spin" : ""}`} />
                      <span className="hidden sm:inline">{t('dashboard.refresh')}</span>
                    </Button>
                  </div>
                </div>

                {/* Contenuto degli itinerari */}
                <div className="relative">
                  {filteredDraftItineraries.length > 0 ? (
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {filteredDraftItineraries.map((itinerary) => (
                        <ItineraryCard
                          key={itinerary.id}
                          itinerary={itinerary}
                          onEdit={handleEditItinerary}
                          onDelete={handleDeleteItinerary}
                          onDuplicate={handleDuplicateItinerary}
                          onViewTravelRequest={handleViewTravelRequest}
                          onViewItinerary={handleEditItinerary}
                          onUpdateStatus={updateItineraryStatus}
                          travelRequests={travelRequests}
                          isAdmin={userRole === "admin"}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-600 mb-4">
                        {selectedDesigner
                          ? t('dashboard.noDraftItinerariesForDesigner', { designer: selectedDesigner })
                          : t('dashboard.noDraftItinerariesYet')
                        }
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        {selectedDesigner
                          ? t('dashboard.tryDifferentDesigner')
                          : t('dashboard.createFirstItinerary')
                        }
                      </p>
                      {selectedDesigner && (
                        <Button
                          variant="outline"
                          onClick={() => setSelectedDesigner(null)}
                          className="mt-2"
                        >
                          {t('dashboard.clearFilter')}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Overlay di caricamento */}
                  {itinerariesLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10 rounded-lg">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                  )}
                </div>

                {/* Errore */}
                {itinerariesError && (
                  <div className="text-center py-12">
                    <p className="text-red-600">{t('dashboard.loadItinerariesError')}</p>
                    <Button onClick={handleRefreshItineraries} className="mt-4">
                      {t('dashboard.retry')}
                    </Button>
                  </div>
                )}
              </div>
            </ProfileTabsContent>
          </Tabs>
        </div>
      </div>

      {/* Modale per i dettagli della richiesta */}
      <TravelRequestModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTravelRequest(null);
        }}
        travelRequest={selectedTravelRequest}
        onAccept={async (id: string) => {
          const result = await acceptTravelRequest(id);
          return result;
        }}
        onDecline={async (id: string) => {
          const result = await declineTravelRequest(id);
          return result;
        }}
        showActions={userRole === 'travel_designer' && selectedTravelRequest?.status === 'pending' || false}
      />

      {/* Modal per la creazione di itinerario */}
      <Modal
        isOpen={isCreateItineraryModalOpen}
        onClose={handleCancelItineraryCreation}
        title=""
        size="sm"
        showHeader={false}
        footer={
          <div className="flex items-center justify-between w-full">
            <Button
              variant="ghost"
              onClick={handleCancelItineraryCreation}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleStartBuilding}
              className="flex items-center gap-2"
            >
              {t('dashboard.startBuilding')}
            </Button>
          </div>
        }
      >
        <div className="text-center py-20">
          <div className="flex justify-center mb-4">
            <Map className="h-8 w-8" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('dashboard.readyToCreate')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('dashboard.eachItineraryIndependent')}
          </p>
        </div>
      </Modal>
    </PrivateLayout>
  );
};

export default Dashboard;
